import { read_local_storage, safeJSONParse, update_local_storage } from "@tellescope/utilities"
import { useCallback, useEffect, useState } from "react"

export const usePolling = (action: Function, delayInMS: number) => {
  useEffect(() => {
    const i = setInterval(async () => {
      try {
        await action()
      } catch(err) {
        console.error('error polling', err)
      }
    }, delayInMS)
    return () => { clearInterval(i) }
  }, [action, delayInMS])
}

export const useCachedState = <T extends string | boolean | number | object | undefined>({ 
  key, 
  initialValue,
  customDeserialize,
} : { 
  key: string,
  initialValue: T,
  customDeserialize?: (s: string) => T,
}) => {
  const deserialize = (
    typeof initialValue === 'boolean'
      ? 'boolean'
  : typeof initialValue === 'object'
      ? 'object'
      : 'string'
  )
  
  const cached = read_local_storage(key)
  const deserialized = (
    cached 
      ? customDeserialize
        ? customDeserialize(cached)
        : deserialize === 'object'
          ? (safeJSONParse(cached) || undefined)
          : deserialize === 'boolean'
            ? cached === 'true'
            : cached
      : undefined
  )

  const [state, _setState] = useState<T>(
    // keep false and '' over initialValue
    deserialized ?? initialValue 
  )

  const setState = useCallback((v: T) => {
    update_local_storage(
      key, 
      deserialize === 'object'
        ? JSON.stringify(v)   
        : (
          v === undefined
            ? 'undefined'
            : v.toString()
        )
    )
    _setState(v)
  }, [key, deserialize])

  return [state, setState] as const
}